import {Injectable} from '@angular/core';

/**
 * Result object returned by calculateTotals().
 * The component applies these values to its view model.
 */
export interface TransactionTotalsResult {
  subtotal: number;
  taxtotal: number;
  total: number;
  showTaxes: boolean;
  invoiceTaxes: InvoiceTax[];
}

/**
 * A single tax code summary entry used in the tax breakdown display.
 */
export interface InvoiceTax {
  taxCode: string;
  taxName: string;
  taxAmount: number;
}

/**
 * TransactionCalculationService
 *
 * Responsible for all line-level and document-level financial calculations
 * across all transaction types (AR Invoice, AP Invoice, Sales Order,
 * Purchase Order, Quote, Shipment).
 *
 * All methods are pure or near-pure: they accept data as parameters and
 * return results — they do not read from or write to component state.
 * This makes them independently unit-testable without a component fixture.
 *
 * Phase 1 of the Invoice Form Refactor — see docs/Invoice Form Refactor.md
 */
@Injectable({
  providedIn: 'root'
})
export class TransactionCalculationService {

  /**
   * Normalize a raw input value (from a form field or event) to a number
   * rounded to 2 decimal places. Returns 0 for any non-numeric input.
   *
   * Replaces: invoice-form.changeToDecimal()
   */
  changeToDecimal(value: any): number {
    let inputValue = '';
    try {
      if (value && typeof value === 'object') {
        if (value instanceof Event) {
          const v = (value.target as HTMLInputElement)?.value;
          inputValue = (v ?? '').toString();
        } else if ('value' in value) {
          inputValue = ((value as HTMLInputElement).value ?? '').toString();
        } else {
          inputValue = (value as any)?.toString?.() ?? '';
        }
      } else {
        inputValue = (value ?? '').toString();
      }

      inputValue = inputValue.replace(/,/g, '').trim();
      const num = parseFloat(inputValue);

      if (!Number.isFinite(num)) {
        return 0;
      }

      return Math.round(num * 100) / 100;
    } catch {
      return 0;
    }
  }

  /**
   * Calculate the subtotal for a single detail line.
   * subtotal = amount * quantity (no tax, no discount at line level).
   *
   * Replaces: inline calculation inside invoice-form.calcTotal()
   */
  calculateLineSubtotal(detail: any): number {
    if (!detail.amount || !detail.quantity) {
      return 0;
    }
    return Math.round(detail.amount * detail.quantity * 100) / 100;
  }

  /**
   * Calculate the line total for display purposes (quantity × unit_price).
   * Used when building processed line objects.
   *
   * Replaces: invoice-form.calculateLineTotal()
   */
  calculateLineTotal(line: any): number {
    const quantity = line.quantity ?? 0;
    const unitPrice = line.unit_price ?? 0;
    return quantity * unitPrice;
  }

  /**
   * Sum the subtotals across all detail lines.
   *
   * Replaces: invoice-form.calculateSubTotal()
   */
  calculateSubTotal(details: any[]): number {
    if (!details?.length) {
      return 0;
    }
    return details.reduce((total, line) => total + (line.amount ?? 0), 0);
  }

  /**
   * Sum the tax amounts across all detail lines.
   *
   * Replaces: invoice-form.calculateTaxTotal()
   */
  calculateTaxTotal(details: any[]): number {
    if (!details?.length) {
      return 0;
    }
    return details.reduce((total, line) => total + (line.taxAmount ?? 0), 0);
  }

  /**
   * Compute the grand total from pre-calculated subtotal and tax total.
   *
   * Replaces: invoice-form.calculateGrandTotal()
   */
  calculateGrandTotal(subtotal: number, taxTotal: number): number {
    return Number(subtotal) + Number(taxTotal);
  }

  /**
   * Compute the discount amount from the subtotal and a percentage.
   *
   * Replaces: invoice-form.calculateDiscountAmount()
   */
  calculateDiscountAmount(subtotal: number, discountPercent: number): number {
    return subtotal * (discountPercent / 100);
  }

  /**
   * Process tax for a single detail line and accumulate into the running totals.
   * Returns the tax amount added (0 if no tax applies).
   *
   * @param detail       The detail line object
   * @param taxNumber    1 or 2 (supports dual-tax lines)
   * @param taxList      The loaded list of tax objects (vm.tax)
   * @param invoiceTaxes The running tax breakdown array (mutated in place)
   */
  processTax(
    detail: any,
    taxNumber: 1 | 2,
    taxList: any[],
    invoiceTaxes: InvoiceTax[]
  ): number {
    const taxIdField = `tax_id_${taxNumber}`;
    const taxAmountField = `tax_amount_${taxNumber}`;

    if (!detail[taxIdField] || !taxList?.length) {
      return 0;
    }

    const taxAmount = Number(detail[taxAmountField] ?? 0);

    const taxObj = taxList.find(t => t.id === detail[taxIdField]);
    if (!taxObj) {
      return taxAmount;
    }

    const taxCode = taxObj.code;
    const taxName = taxObj.name;

    const existingIndex = invoiceTaxes.findIndex(t => t.taxCode === taxCode);
    if (existingIndex >= 0) {
      invoiceTaxes[existingIndex].taxAmount += taxAmount;
    } else {
      invoiceTaxes.push({taxCode, taxName, taxAmount});
    }

    return taxAmount;
  }

  /**
   * Calculate all totals for a transaction document in a single pass over
   * the detail lines. Returns a result object; the component applies the
   * values to its view model.
   *
   * Replaces: invoice-form.calcTotal()
   *
   * @param details  The vm.item.detail array
   * @param taxList  The vm.tax array (loaded tax reference list)
   */
  calculateTotals(details: any[], taxList: any[]): TransactionTotalsResult {
    const invoiceTaxes: InvoiceTax[] = [];

    const totals = (details ?? []).reduce((acc, detail) => {
      if (!detail.amount || !detail.quantity) {
        return acc;
      }

      acc.subtotal += this.calculateLineSubtotal(detail);
      acc.taxtotal += this.processTax(detail, 1, taxList, invoiceTaxes);
      acc.taxtotal += this.processTax(detail, 2, taxList, invoiceTaxes);

      return acc;
    }, {subtotal: 0, taxtotal: 0});

    return {
      subtotal: totals.subtotal,
      taxtotal: totals.taxtotal,
      total: this.calculateGrandTotal(totals.subtotal, totals.taxtotal),
      showTaxes: totals.taxtotal > 0,
      invoiceTaxes
    };
  }
}
